import clsx from 'clsx';
import { X } from 'lucide-react';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.scss';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    className?: string;
    width?: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, className, width }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      className={styles.overlay}
      onClick={onClose}
    >
      <div className={styles.container}>
        <div 
          className={clsx(
            styles.modal,
            width ? width : '', // width prop is now expected to be a valid css class or removed if we rely on scss completely. 
            // Since user wants no tailwind, 'max-w-md' might not work unless we map it. 
            // For now, let's assume width prop is handled via inline style or custom class if provided, but default to scss width handling.
            // But to support max-w-sm passed from parent, we might need to map it or ask user to change parent.
            // Let's check parent usage: width="max-w-sm". This is tailwind.
            // We should probably just hardcode a style for now or map it.
            className
          )}
          style={{ maxWidth: width === 'max-w-sm' ? '24rem' : '28rem' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={styles.header}>
            <h3 className={styles.title}>{title}</h3>
            {/* Close Button */}
            <button 
              onClick={onClose}
              className={styles.closeButton}
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
